import { Box, Circle, HStack, Stack, Text } from '@chakra-ui/react';
import { CalendarClock, CameraFlash, Contactless } from '@/client/components/icons/ContinuIcons';

import type { AssignmentFormData } from '../../types/assignmentTypes';
import AssignmentFormPreviewHeader from './AssignmentFormPreviewHeader';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { useAdminAssignments } from '../../hooks/useAdminAssignments';
import { useTranslation } from 'react-i18next';

interface AutomationsFormPreviewProps {
  settings: AssignmentFormData['settings'];
  renderStatus?: boolean;
}

export default function AutomationsFormPreview({
  settings,
  renderStatus = false,
}: AutomationsFormPreviewProps) {
  const { t } = useTranslation();
  const { automationTypeTranslationMap } = useAdminAssignments();

  const { triggerObject, objectNumber, conditionals, type } = settings;

  const includesDays =
    type === WorkflowType.Hired_60 ||
    type === WorkflowType.Hired_90 ||
    type === WorkflowType.HiredCustom;

  const renderConditionals =
    (type === WorkflowType.UserAddedOrUpdated || type === WorkflowType.UserUpdated) &&
    conditionals?.length > 0;

  const hasTriggerObject = !!triggerObject?.id && !!triggerObject?.title;

  const dayCountMap = {
    [WorkflowType.Hired_60]: 60,
    [WorkflowType.Hired_90]: 90,
    [WorkflowType.HiredCustom]: objectNumber,
  };

  return (
    <Box>
      <AssignmentFormPreviewHeader
        formName="automations"
        icon={Contactless}
        title={t('admin.assignments.automation')}
        renderStatus={renderStatus}
      />

      {type !== null && type !== WorkflowType.PointInTime && (
        <Box padding={6} borderTop="1px solid" borderTopColor="neutral.100">
          <Stack spacing={4}>
            <HStack>
              <Circle size={5} backgroundColor="warmNeutral.200">
                <CameraFlash boxSize={3} color="warmNeutral.1200" />
              </Circle>

              <Text fontSize="14px" fontWeight={500}>
                {automationTypeTranslationMap[type]}{' '}
                {renderConditionals && (
                  <Text as="span">
                    ({conditionals.map((conditional) => conditional.name).join(', ')})
                  </Text>
                )}
              </Text>
            </HStack>

            {includesDays && (
              <HStack>
                <Circle size={5} backgroundColor="warmNeutral.200">
                  <CalendarClock boxSize={3} color="warmNeutral.1200" />
                </Circle>

                <Text fontSize="14px" fontWeight={500}>
                  {`${dayCountMap[type]} ${t('admin.assignments.automations.preview.daysAgo')}`}
                </Text>
              </HStack>
            )}

            {hasTriggerObject && (
              <Text fontSize="14px" fontWeight={500}>
                {triggerObject.title}
              </Text>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
